import React from 'react'
import { motion } from 'framer-motion'
import { 
  Zap, 
  ThumbsUp, 
  ThumbsDown, 
  Activity, 
  AlertTriangle,
  Merge
} from 'lucide-react'
import { FoodSynergy } from '../../services/mealmateService'
import { Badge } from '../ui/Badge'
import { Progress } from '../ui/Progress'

interface SynergyAnalysisProps {
  synergy: FoodSynergy
}

const SynergyAnalysis: React.FC<SynergyAnalysisProps> = ({ synergy }) => {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-theme-success'
    if (score >= 60) return 'text-theme-primary'
    if (score >= 40) return 'text-theme-warning'
    return 'text-theme-error'
  }

  const getScoreBarColor = (score: number) => {
    if (score >= 80) return 'theme-success'
    if (score >= 60) return 'theme-primary'
    if (score >= 40) return 'theme-warning'
    return 'theme-error'
  }

  const getInteractionIcon = (type: string, effect: string) => {
    if (type === 'positive') {
      switch (effect) {
        case 'bioavailability': return <Zap className="w-3 h-3 text-yellow-500" />
        case 'digestion': return <Activity className="w-3 h-3 text-green-500" />
        default: return <ThumbsUp className="w-3 h-3 text-theme-primary" />
      }
    } else {
      switch (effect) {
        case 'toxicity': return <AlertTriangle className="w-3 h-3 text-red-500" />
        default: return <ThumbsDown className="w-3 h-3 text-orange-500" />
      }
    }
  }

  return (
    <div className="space-y-4 p-4 bg-theme-surface-light rounded-xl border border-theme-border">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold text-theme-text-primary flex items-center gap-2">
          <Merge className="w-4 h-4 text-theme-accent" />
          Nutrient Synergy
        </h4>
        <div className={`text-lg font-black ${getScoreColor(synergy.score)}`}>
          {synergy.score}/100
        </div>
      </div>

      <Progress value={synergy.score} className="h-2" indicatorClassName={`bg-${getScoreBarColor(synergy.score)}`} />
      
      <p className="text-xs text-theme-text-secondary italic">
        {synergy.summary}
      </p>

      {/* Interactions List */}
      <div className="space-y-3 pt-2">
        {synergy.interactions.map((item, idx) => (
          <motion.div 
            key={idx}
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`flex flex-col gap-1 p-3 rounded-lg border ${
              item.type === 'positive' 
                ? 'bg-green-500/5 border-green-500/20' 
                : 'bg-red-500/5 border-red-500/20'
            }`}
          >
            <div className="flex items-center justify-between">
               <div className="flex items-center gap-2">
                  {getInteractionIcon(item.type, item.effect)}
                  <span className={`text-xs font-bold uppercase tracking-wider ${
                    item.type === 'positive' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                  }`}>
                    {item.effect}
                  </span>
               </div>
               <Badge variant="outline" className="text-[10px] h-5 bg-theme-surface">
                 {item.ingredients.join(' + ')}
               </Badge>
            </div>
            
            <p className="text-xs text-theme-text-primary mt-1">
              {item.description}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

export default SynergyAnalysis
